/** 
 * @module SEEMPMonitoringPlanII
 * @description SEEMP II Monitoring Plan component for generating templated plan.
 * This is a child component branched out from PlanGenerator.js to handle only SEEMP II Monitoring Plans
 */
import React, { useEffect, useState, useContext } from "react";
import {
    Accordion,
    AccordionSummary,
    AccordionDetails,
} from "@mui/material";
import { FormProvider, useForm } from "react-hook-form";

import TotalDistanceTracker from "./TotalDistanceTracker/TotalDistanceTracker";
import MeasureHours from "./MeasureHours/MeasureHours";
import DataReportAdministrator from "./DataReportAdminstrator/DataReportAdminstrator";
import DataQuality from "./DataQuality/DataQuality";
import FuelConsCollectionPlanRevisions from "./FuelConsCollectionPlanRevisions/FuelConsCollectionPlanRevisions";
import FuelOilConsMeasurmentMethod from "./FuelOilConsMeasurementMethod/FuelOilConsMeasurementMethod";
import FuelOilEnginesOrOtherFuelConsumerType from "./EnginesOrOtherFuelTypeConsumer/EnginesOrOtherFuelTypeConsumer";
import ShipParticulars from "./ShipParticulars/ShipParticular";
import ShipEnginesAndFuelConsumers from "./ShipEnginesAndFuelConsumers/ShipEnginesAndFuelConsumers";
import EmissionFactors from "./EmissionFactor/EmissionFactor";
import FuelOilConsumption from "./FuelOilConsumption/FuelOilConsumption";
import OnshorePower from "./OnshorePower/OnshorePower";
import TotalTransportWork from "./TotalTransportWork/TotalTransportWork";
import Appendix from "../SeempII/Appendix/Appendix";
import SeempIIReviewAndUpdateLog from "./ReviewAndUpdateLog/ReviewAndUpdateLog";

import { API_ROUTES } from "../ApiRoutes/ApiRoutes";
import { callApi, getVesselById } from "../../../../services/MonitoringPlanService";
import { Button, Icon, ICONS } from "abs-ui-components";
import { emitToasterMsg } from "../../../../services/toasterUtility";
import axiosHttp from "../../../../services/interceptor";
import { ClaimsContext } from "../../../../services/ClaimsService";
import { FeatureType } from "../../../../constants/FeatureToggles";
import CloneModal from "../../CloneModal";
import ResultSummaryModal from "../../ResultSummaryModal";

/**
 * @param {object} props
 * @param {number|string} props.vesselMstId
 * @param {string} props.status
 * @param {object} props.planVerificationDetails
 * @param {object} props.planVerificationFlags
 * @param {string} props.wcnNumber
 * @param {Function} props.refreshState callback to refresh parent state
 */
const SeempMonitoringPlanII = ({
    vesselMstId,
    status,
    planVerificationDetails,
    planVerificationFlags,
    wcnNumber,
    refreshState,
}) => {
    // UI state
    const [expandedMainPanel, setExpandedMainPanel] = useState("panel1");
    const [expandedChild, setExpandedChild] = useState(null);

    // Data state
    const [monitoringData, setMonitoringData] = useState();
    const [preFilldata, setPreFilldata] = useState();
    const [vesselData, setVesselData] = useState(null);

    // Process/UI flags
    const [loading, setLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showCloneModal, setShowCloneModal] = useState(false);

    const [selectedSections, setSelectedSections] = useState([]);
    const [selectedVessels, setSelectedVessels] = useState([]);
    const [confirmationChecked, setConfirmationChecked] = useState(false);
    const { userHasFeature } = useContext(ClaimsContext);
    const [cloneType, setCloneType] = useState("");
    const [sectionsList, setSectionsList] = useState([]);
    const [showResultModal, setShowResultModal] = useState(false);
    const [resultSummaryData, setResultSummaryData] = useState([]);

    // Form context
    const methods = useForm({
        mode: "onBlur",
        defaultValues: {
            NameOfTheShip: "",
        },
    });

    // Permission flag
    const canSubmitDocuments =
        planVerificationDetails?.VerificationStatus === "Draft" ||
        planVerificationDetails?.VerificationStatus === "Retained" ||
        (planVerificationDetails?.VerificationStatus === "Under Review" &&
            planVerificationFlags?.data?.hasCorrections);

    /* ------------------------- UI Helpers ------------------------- */

    const renderTitleWithIcon = (panel, title) => (
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <span style={{ fontSize: "20px", fontWeight: "bold" }}>
                {expandedMainPanel === panel || expandedChild === panel ? "−" : "+"}
            </span>
            <p className="fuel-cons">{title}</p>
        </div>
    );

    const handleMainChange = (panel) => (_, isExpanded) => {
        setExpandedMainPanel(isExpanded ? panel : false);
    };

    /* ------------------------- Data Fetchers ------------------------- */

    const fetchMonitoringData = async () => {
        try {
            const response = await callApi({
                method: "GET",
                url: API_ROUTES.GET_SEEMP_MONITORINGPLAN_II_BY_VESSELID(vesselMstId),
                data: {},
            });
            if (response) setMonitoringData(response);
        } catch (error) {
            console.error("Error fetching monitoring plan data", error);
        } finally {
            setLoading(false);
        }
    };

    const fetchSeempPreFillData = async () => {
        try {
            const response = await callApi({
                method: "GET",
                url: API_ROUTES.GET_SEEMP_PREFILLDATA(vesselMstId),
                data: {},
            });
            if (response) setPreFilldata(response);
        } catch (error) {
            console.error("Error fetching data", error);
        } finally {
            setLoading(false);
        }
    };

    const fetchVesselData = async () => {
        try {
            const response = await getVesselById(vesselMstId);
            if (response) setVesselData(response);
        } catch (error) {
            console.error("Error fetching vessel data", error);
        } finally {
            setLoading(false);
        }
    };

    // Initial load
    useEffect(() => {
        if (vesselMstId) {
            fetchSeempPreFillData();
            fetchMonitoringData();
            fetchVesselData();
        }
    }, [vesselMstId]);

    /* ------------------------- Validation Wrapper ------------------------- */

    const validateAndExecute = async (apiUrl, onSuccess, errorTitle) => {
        if (!vesselMstId) return;
        try {
            const res = await callApi({ method: "GET", url: apiUrl, data: {} });
            if (res?.isValid) {
                await onSuccess();
            } else {
                emitToasterMsg("error", errorTitle, res?.errors || "Validation failed");
            }
        } catch (err) {
            emitToasterMsg("error", errorTitle, err?.errors || "Something went wrong");
        }
    };

    /* ------------------------- Actions ------------------------- */

    const handleDownloadPDF = async () => {
        try {
            const res = await callApi({
                method: "POST",
                url: `${API_ROUTES.GET_SEEMP_DOWNLOAD_PDF(vesselMstId)}?planType=seempii`,
                data: {},
                responseType: "blob",
            });
            if (res) {
                const url = window.URL.createObjectURL(new Blob([res], { type: "application/pdf" }));
                const link = document.createElement("a");
                link.href = url;
                link.setAttribute("download", `SEEMP II plan ${vesselData.ImoNumber}.pdf`);
                document.body.appendChild(link);
                link.click();
                link.remove();
                emitToasterMsg("success", "PDF", "PDF downloaded successfully");
            }
        } catch (err) {
            console.error("Error downloading PDF", err);
            emitToasterMsg("error", "PDF", "Failed to download PDF");
        }
    };

    const handleSubmitSeempIIMonitoringPlan = async () => {
        try {
            setIsSubmitting(true);
            const planId = planVerificationDetails?.PlanId;
            if (planId) {
                await axiosHttp({
                    url: `api/PlanVerification/SubmitToDocuments?planId=${encodeURIComponent(planId)}&wcnNumber=${encodeURIComponent(wcnNumber)}`,
                    method: "GET",
                });
                emitToasterMsg("success", "Documents", "Submitted to Documents");
            } else {
                emitToasterMsg("error", "Documents", "Failed to submit to Documents, could not determine PlanId");
            }
        } catch (err) {
            console.error("Error calling SubmitToDocuments", err);
            emitToasterMsg("error", "Documents", "Failed to submit to Documents");
        } finally {
            setIsSubmitting(false);
            refreshState?.();
        }
    };

    const handleValidate = async (planType) => {
        try {
            const res = await callApi({
                method: "POST",
                url: API_ROUTES.Validate_Plan("SEEMP II", vesselMstId),
                data: {}
            });

            if (res?.isValid) {
                await fetchSections();
                emitToasterMsg("success", `${planType} Clone`, "Validate successfully");
                setCloneType(planType);
                setShowCloneModal(true);
            }
            else {
                emitToasterMsg("error", `${planType} Clone`, res?.errors || "Validate failed");
            }
        } catch (err) {
            emitToasterMsg("error", `${planType} Clone`, "Validate failed");
        }
    }

    const fetchSections = async () => {
        try {
            const res = await callApi({
                method: "GET",
                url: API_ROUTES.Get_Sections_By_PlanType("SEEMP II")
            });

            // Store in your modal's list state
            setSectionsList(res.sections);

        } catch (error) {
            console.error("Failed to load sections", error);
            emitToasterMsg("error", "Sections", "Failed to load sections");
        }
    };

    // Validation entry points
    const CheckPdfDownloadEnable = () =>
        validateAndExecute(
            API_ROUTES.GET_ENABLE_SEEMPII_DOWNLOAD_PDF(vesselMstId),
            handleDownloadPDF,
            "PDF"
        );

    const CheckSeempIIValidations = () =>
        validateAndExecute(
            API_ROUTES.GET_ENABLE_SEEMPII_DOWNLOAD_PDF(vesselMstId),
            handleSubmitSeempIIMonitoringPlan,
            "SEEMPII"
        );

    /* ------------------------- Render ------------------------- */

    return (
        <div>
            {/* Action buttons */}
            <div className="d-flex justify-content-end">
                <>
                 {userHasFeature?.(FeatureType.SEEMPIIClone) && (
                    <Button
                        className="mr-2"
                        onClick={() => {
                            handleValidate("SEEMP II")
                        }}
                    >
                        SEEMP II Clone
                    </Button>
                 )}
                    <Button
                        className="mr-4"
                        startIcon={<Icon icon={ICONS.DOWNLOAD} />}
                        onClick={CheckPdfDownloadEnable}
                    >
                        Download PDF
                    </Button>
                    {userHasFeature?.(FeatureType.SubmitToDocuments) && (
                        <Button
                            className="mr-4"
                            onClick={CheckSeempIIValidations}
                            disabled={!canSubmitDocuments || isSubmitting}
                        >
                            Submit to Documents
                        </Button>
                    )}
                </>
            </div>

            {/* Form wrapper for potential React Hook Form usage */}
            <FormProvider {...methods}>
                <form className="activity-data">
                    {/* 1. Review and update log */}
                    <Accordion
                        expanded={expandedMainPanel === "panel1"}
                        onChange={handleMainChange("panel1")}
                        className="mt-3"
                    >
                        <AccordionSummary className="accordion-header">
                            {renderTitleWithIcon("panel1", "1. Review and update log")}
                        </AccordionSummary>
                        <AccordionDetails>
                            <fieldset
                                disabled={!canSubmitDocuments}
                                style={{ border: 0, padding: 0, margin: 0 }}
                            >
                                <SeempIIReviewAndUpdateLog
                                    seempIIReviewAndUpdateLog={monitoringData?.reviewAndUpdateLogs}
                                    refetchMonitoringData={fetchMonitoringData}
                                    vesselMstId={vesselMstId}
                                />
                            </fieldset>
                        </AccordionDetails>
                    </Accordion>

                    {/* 2. Ship particulars */}
                    <Accordion
                        expanded={expandedMainPanel === "panel2"}
                        onChange={handleMainChange("panel2")}
                        className="mt-3"
                    >
                        <AccordionSummary className="accordion-header">
                            {renderTitleWithIcon("panel2", "2. Ship particulars")}
                        </AccordionSummary>
                        <AccordionDetails>
                            <fieldset
                                disabled={!canSubmitDocuments}
                                style={{ border: 0, padding: 0, margin: 0 }}
                            >
                                <ShipParticulars
                                    vesselDetails={vesselData}
                                    monitoringData={monitoringData?.shipParticulars}
                                    vesselMstId={vesselMstId}
                                />
                            </fieldset>
                        </AccordionDetails>
                    </Accordion>

                    {/* 3. Fuel Oil Consumption Data-Collection Plan revisions */}
                    <Accordion
                        expanded={expandedMainPanel === "panel3"}
                        onChange={handleMainChange("panel3")}
                        className="mt-3"
                    >
                        <AccordionSummary className="accordion-header">
                            {renderTitleWithIcon(
                                "panel3",
                                "3.Record of revision of Fuel Oil Consumption Data-Collection Plan"
                            )}
                        </AccordionSummary>
                        <AccordionDetails>
                            <AccordionDetails>
                                <fieldset
                                    disabled={!canSubmitDocuments}
                                    style={{ border: 0, padding: 0, margin: 0 }}
                                >
                                    <FuelConsCollectionPlanRevisions
                                        fuelConsCollectionPlanRevisions={
                                            monitoringData?.fuelConsCollectionPlanRevisions
                                        }
                                        refetchMonitoringData={fetchMonitoringData}
                                        vesselMstId={vesselMstId}
                                    />
                                </fieldset>
                            </AccordionDetails>
                        </AccordionDetails>
                    </Accordion>

                    {/* 4. Engines and fuel consumers */}
                    <Accordion
                        expanded={expandedMainPanel === "panel4"}
                        onChange={handleMainChange("panel4")}
                        className="mt-3"
                    >
                        <AccordionSummary className="accordion-header">
                            {renderTitleWithIcon(
                                "panel4",
                                "4. Ship engines and other fuel oil consumers and fuel oil types used"
                            )}
                        </AccordionSummary>
                        <AccordionDetails>
                            <fieldset
                                disabled={!canSubmitDocuments}
                                style={{ border: 0, padding: 0, margin: 0 }}
                            >
                                <ShipEnginesAndFuelConsumers
                                    vesselDetails={vesselData}
                                    PreFillData={preFilldata}
                                    vesselMstId={vesselMstId}
                                    refetchMonitoringData={fetchMonitoringData}
                                    ShipEngines={monitoringData}
                                />
                            </fieldset>
                        </AccordionDetails>
                    </Accordion>

                    {/* 5. Emission factor */}
                    <Accordion
                        expanded={expandedMainPanel === "panel5"}
                        onChange={handleMainChange("panel5")}
                        className="mt-3"
                    >
                        <AccordionSummary className="accordion-header">
                            {renderTitleWithIcon("panel5", "5. Emission factor")}
                        </AccordionSummary>
                        <AccordionDetails>
                            <fieldset
                                disabled={!canSubmitDocuments}
                                style={{ border: 0, padding: 0, margin: 0 }}
                            >
                                <EmissionFactors
                                    vesselDetails={vesselData}
                                    refetchMonitoringData={monitoringData}
                                    vesselMstId={vesselMstId}
                                />
                            </fieldset>
                        </AccordionDetails>
                    </Accordion>

                    {/* 6. Fuel oil consumption & methods */}
                    <Accordion
                        expanded={expandedMainPanel === "panel6"}
                        onChange={handleMainChange("panel6")}
                        className="mt-3"
                    >
                        <AccordionSummary className="accordion-header">
                            {renderTitleWithIcon(
                                "panel6",
                                "6.  Method to measure fuel oil consumption including hours not  under way"
                            )}
                        </AccordionSummary>
                        <AccordionDetails>
                            <fieldset
                                disabled={!canSubmitDocuments}
                                style={{ border: 0, padding: 0, margin: 0 }}
                            >
                                <FuelOilConsumption
                                    fuelOilConsumptiondescription={
                                        monitoringData?.fuelOilConsumptionDescription
                                    }
                                    refetchMonitoringData={fetchMonitoringData}
                                    vesselMstId={vesselMstId}
                                />
                            </fieldset>
                        </AccordionDetails>
                        <AccordionDetails>
                            <fieldset
                                disabled={!canSubmitDocuments}
                                style={{ border: 0, padding: 0, margin: 0 }}
                            >
                                <FuelOilConsMeasurmentMethod
                                    fuelOilConsumptionMeasurementMethod={
                                        monitoringData?.fuelOilConsumptionMeasurementMethod
                                    }
                                    preFilldata={preFilldata?.fuelOilConsMeasurementData}
                                    refetchMonitoringData={fetchMonitoringData}
                                    vesselMstId={vesselMstId}
                                />
                            </fieldset>
                        </AccordionDetails>
                        <AccordionDetails>
                            <fieldset
                                disabled={!canSubmitDocuments}
                                style={{ border: 0, padding: 0, margin: 0 }}
                            >
                                <FuelOilEnginesOrOtherFuelConsumerType
                                    enginesOrOtherFuelConsumerData={
                                        monitoringData?.engineFuelOilConumerType
                                    }
                                    preFilldata={preFilldata}
                                    refetchMonitoringData={fetchMonitoringData}
                                    vesselMstId={vesselMstId}
                                />
                            </fieldset>
                        </AccordionDetails>
                    </Accordion>

                    {/* 7. Distance travelled */}
                    <Accordion
                        expanded={expandedMainPanel === "panel7"}
                        onChange={handleMainChange("panel7")}
                        className="mt-3"
                    >
                        <AccordionSummary className="accordion-header">
                            {renderTitleWithIcon(
                                "panel7",
                                "7. Method to measure distance travelled including laden distance"
                            )}
                        </AccordionSummary>
                        <AccordionDetails>
                            <fieldset
                                disabled={!canSubmitDocuments}
                                style={{ border: 0, padding: 0, margin: 0 }}
                            >
                                <TotalDistanceTracker
                                    seempIILadendistance={
                                        monitoringData?.totalDistanceTravelledDescription
                                    }
                                    vesselMstId={vesselMstId}
                                    refetchMonitoringData={fetchMonitoringData}
                                />
                            </fieldset>
                        </AccordionDetails>
                    </Accordion>

                    {/* 8. Hours under way */}
                    <Accordion
                        expanded={expandedMainPanel === "panel8"}
                        onChange={handleMainChange("panel8")}
                        className="mt-3"
                    >
                        <AccordionSummary className="accordion-header">
                            {renderTitleWithIcon(
                                "panel8",
                                "8. Method to measure hours under way and hours not under way"
                            )}
                        </AccordionSummary>
                        <AccordionDetails>
                            <fieldset
                                disabled={!canSubmitDocuments}
                                style={{ border: 0, padding: 0, margin: 0 }}
                            >
                                <MeasureHours
                                    seempIIMeasure={monitoringData?.hoursUnderWayDescription}
                                    vesselMstId={vesselMstId}
                                    refetchMonitoringData={fetchMonitoringData}
                                />
                            </fieldset>
                        </AccordionDetails>
                    </Accordion>

                    {/* 9. Reporting processes */}
                    <Accordion
                        expanded={expandedMainPanel === "panel9"}
                        onChange={handleMainChange("panel9")}
                        className="mt-3"
                    >
                        <AccordionSummary className="accordion-header">
                            {renderTitleWithIcon(
                                "panel9",
                                "9. Processes that will be used to report the data to the Administration"
                            )}
                        </AccordionSummary>
                        <AccordionDetails>
                            <fieldset
                                disabled={!canSubmitDocuments}
                                style={{ border: 0, padding: 0, margin: 0 }}
                            >
                                <DataReportAdministrator
                                    seempIIDatareport={
                                        monitoringData?.dataReportingMethodDescription
                                    }
                                    vesselMstId={vesselMstId}
                                    refetchMonitoringData={fetchMonitoringData}
                                />
                            </fieldset>
                        </AccordionDetails>
                    </Accordion>

                    {/* 10. Data quality */}
                    <Accordion
                        expanded={expandedMainPanel === "panel10"}
                        onChange={handleMainChange("panel10")}
                        className="mt-3"
                    >
                        <AccordionSummary className="accordion-header">
                            {renderTitleWithIcon("panel10", "10. Data quality")}
                        </AccordionSummary>
                        <AccordionDetails>
                            <fieldset
                                disabled={!canSubmitDocuments}
                                style={{ border: 0, padding: 0, margin: 0 }}
                            >
                                <DataQuality
                                    seempIIDataquality={monitoringData?.dataqualityDescription}
                                    vesselMstId={vesselMstId}
                                    refetchMonitoringData={fetchMonitoringData}
                                />
                            </fieldset>
                        </AccordionDetails>
                    </Accordion>

                    {/* 11. Onshore power */}
                    <Accordion
                        expanded={expandedMainPanel === "panel11"}
                        onChange={handleMainChange("panel11")}
                        className="mt-3"
                    >
                        <AccordionSummary className="accordion-header">
                            {renderTitleWithIcon(
                                "panel11",
                                "11. Total amount of onshore power supplied"
                            )}
                        </AccordionSummary>
                        <AccordionDetails>
                            <fieldset
                                disabled={!canSubmitDocuments}
                                style={{ border: 0, padding: 0, margin: 0 }}
                            >
                                <OnshorePower
                                    Onshorepower={monitoringData?.onshorePowerDescription}
                                    vesselMstId={vesselMstId}
                                    refetchMonitoringData={fetchMonitoringData}
                                />
                            </fieldset>
                        </AccordionDetails>
                    </Accordion>

                    {/* 12. Transport work */}
                    <Accordion
                        expanded={expandedMainPanel === "panel12"}
                        onChange={handleMainChange("panel12")}
                        className="mt-3"
                    >
                        <AccordionSummary className="accordion-header">
                            {renderTitleWithIcon("panel12", "12.Total transport work")}
                        </AccordionSummary>
                        <AccordionDetails>
                            <fieldset
                                disabled={!canSubmitDocuments}
                                style={{ border: 0, padding: 0, margin: 0 }}
                            >
                                <TotalTransportWork
                                    Totalwork={monitoringData?.transportworkDescription}
                                    vesselMstId={vesselMstId}
                                    refetchMonitoringData={fetchMonitoringData}
                                />
                            </fieldset>
                        </AccordionDetails>
                    </Accordion>

                    {/* 13. Appendix */}
                    <Accordion
                        expanded={expandedMainPanel === "panel13"}
                        onChange={handleMainChange("panel13")}
                        className="mt-3"
                    >
                        <AccordionSummary className="accordion-header">
                            {renderTitleWithIcon("panel13", "Appendix 1")}
                        </AccordionSummary>
                        <AccordionDetails>
                            <fieldset
                                disabled={!canSubmitDocuments}
                                style={{ border: 0, padding: 0, margin: 0 }}
                            >
                                <Appendix
                                    appendix={monitoringData?.appendixDescription}
                                    refetchMonitoringData={fetchMonitoringData}
                                    vesselMstId={vesselMstId}
                                />
                            </fieldset>
                        </AccordionDetails>
                    </Accordion>
                </form>
            </FormProvider>

            <div className="seemp-clone-modal-popup">
                <CloneModal
                    isOpen={showCloneModal}
                    headerTitle={"SEEMP II Clone"}
                    vesselMstId={vesselMstId}
                    onClose={() => {
                        setShowCloneModal(false);
                        setSelectedSections([]);
                        setSelectedVessels([]);
                        setConfirmationChecked(false);
                    }}
                    planType={cloneType}
                    sectionsList={sectionsList || []}
                    // vesselsList={allDropdownData?.vessels || []}

                    selectedSections={selectedSections}
                    setSelectedSections={setSelectedSections}

                    selectedVessels={selectedVessels}
                    setSelectedVessels={setSelectedVessels}

                    confirmationChecked={confirmationChecked}
                    setConfirmationChecked={setConfirmationChecked}

                    onClone={async () => {
                        const payload = {
                            "planType": cloneType,
                            "vesselMstId": vesselMstId,
                            "cloningOptionIds": [
                                ...selectedSections
                            ],
                            "targetVesselMstIds": selectedVessels?.map(v => v.vesselMstId),
                        }
                        console.log("Clone Payload: ", payload);
                        // API Call Here
                        try {
                            const res = await callApi({
                                method: "POST",
                                url: API_ROUTES.CLONE_PLAN(),
                                data: payload
                            });

                            if (res?.isSuccess) {
                                emitToasterMsg("success", `${cloneType} Clone`, "Cloned successfully");
                                setShowCloneModal(false);
                                setSelectedSections([]);
                                setSelectedVessels([]);
                                setConfirmationChecked(false);
                                setShowResultModal(true);
                                setResultSummaryData(res?.cloneSummaries || []);
                            }
                            else {
                                emitToasterMsg("error", `${cloneType} Clone`, "Clone failed");
                            }
                        } catch (err) {
                            emitToasterMsg("error", `${cloneType} Clone`, "Clone failed");
                        }
                    }}
                />

            </div>

            <div className="clone-result-summary-modal-popup">
                <ResultSummaryModal
                    isOpen={showResultModal}
                    onClose={() => setShowResultModal(false)}
                    resultSummaryData={resultSummaryData || []}
                    headerTitle={"SEEMP II Clone - Result Summary"}
                    subHeaderTitle={"Clone result for selected target vessels"}
                    successMsg={"SEEMP II"}
                />
            </div>
        </div>
    );
};

export default SeempMonitoringPlanII;
